<template>
  <section class="map" id="officeTabs">
    <h2 v-if="title" class="map__title wrapper">{{ title }}</h2>

    <div class="map__tabs">
      <div class="wrapper">
        <div class="map__tabs-header">
          <button
            v-for="(item, index) in offices"
            :key="`${index}_${item.title}`"
            class="map__tabs-btn gap-4"
            :class="{ active: activeTab === index }"
            @click="openTab(index)"
          >
            <NuxtIcon class="icon" name="IconMetro" />
            {{ item.title }}
          </button>
        </div>
      </div>
      <div class="map__tabs-wrapper">
        <div class="wrapper">
          <div class="map__tabs-wrapper-wrap">
            <div v-if="currentOffice" class="map__tabs-content">
              <UIPictureTag
                v-if="currentOffice.image"
                is-visible
                :image-original="`${url}${currentOffice.image.source}`"
                :image-seo="{
                  image_title: currentOffice.title,
                  image_alt: currentOffice.title,
                }"
                :image-webp="`${url}${currentOffice.image.webp}`"
              />
              <a
                :href="`tel:${currentOffice.phone}`"
                @click="goalsClickPhone"
                class="map__tabs-phone"
                >{{ currentOffice.phone }}</a
              >
              <div class="map__tabs-item">
                <NuxtIcon name="IconMetro" />
                {{ currentOffice.title }}
              </div>
              <div class="map__tabs-item">
                <NuxtIcon name="IconClock" filled />
                {{ currentOffice.working_time }}
              </div>
              <div class="map__tabs-item">
                <NuxtIcon name="IconMapPin" filled />
                {{ currentOffice.address }}
              </div>

              <div class="map__tabs-btns">
                <UIButton
                  v-if="currentOffice.video_link"
                  variant="primary"
                  @click="openVideoModal"
                >
                  {{ currentOffice.button_text }}
                </UIButton>

                <UIButton
                  type="link"
                  link-type="_blank"
                  variant="primary"
                  :link="`https://yandex.ru/maps/?ll=${currentOffice.longitude}%2C${currentOffice.latitude}&mode=routes&rtext=~${currentOffice.latitude}%2C${currentOffice.longitude}&rtt=pd&z=13 size=l`"
                >
                  <NuxtIcon name="IconRoute" />
                  {{ currentOffice.address }}
                </UIButton>
              </div>
              <CRatingBlock
                v-if="getOfficeRating(currentOffice)"
                :media="getOfficeRating(currentOffice).media"
                :custom-breakpoints="{
                  520: {
                    slidesPerView: 4,
                    spaceBetween: 24,
                    centeredSlides: false,
                  },
                  320: {
                    slidesPerView: 2.5,
                    spaceBetween: 12,
                    centeredSlides: false,
                  },
                }"
                :slides-per-view="3.5"
              />
            </div>
            <div class="map__tabs-map">
              <COfficeMap
                :offices="officeList"
                :active-tab="activeTab"
                :center-coords="centerCoords"
                :location-bounds="locationBounds"
                @open-tab="openTab"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import './styles.scss';
import { useState, useMediaModal, watch, computed, ref, useRuntimeConfig } from '#imports';
import CRatingBlock from '~/components/blocks/ratingBlock/CRatingBlock.vue';
import COfficeMap from '~/components/blocks/officeBlock/COfficeMap.client.vue';
import type { BlockRating, ImageItem } from '~/types/pages';
import { yandexRichGoals } from '~/composables/yandexRichGoals';

interface Office {
  title: string;
  working_time: string;
  address: string;
  phone: string;
  latitude: number;
  longitude: number;
  image: ImageItem;
  blocks: BlockRating;
  button_text: string;
  video_link: string;
}

defineProps<{
  title?: string;
}>();

const offices = useState<Office[]>('affiliates');
const officeList = computed(() => (Array.isArray(offices.value) ? offices.value : []));
const url = useRuntimeConfig().public.siteUrl;
const { goalsClickPhone } = yandexRichGoals();
const activeTab = ref(0);
const currentOffice = computed(() => officeList.value[activeTab.value] ?? null);

function getOfficeRating(office: Office) {
  return office.blocks?.find(el => el.resourcetype === 'OfficeBlockRating');
}

const centerCoords = computed(() => {
  const filteredOffices = officeList.value.filter(
    item => item.latitude !== null && item.longitude !== null,
  );

  if (filteredOffices.length === 0) {
    return null;
  }

  const sum = filteredOffices.reduce(
    (acc, coord) => {
      return {
        latitude: Number(acc.latitude) + Number(coord.latitude),
        longitude: Number(acc.longitude) + Number(coord.longitude),
      };
    },
    { latitude: 0, longitude: 0 },
  );

  return {
    latitude: sum.latitude / filteredOffices.length,
    longitude: sum.longitude / filteredOffices.length,
  };
});

const openTab = (index: number) => {
  activeTab.value = index;

  if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
    document.getElementById('officeTabs')?.scrollIntoView({
      behavior: 'smooth',
    });
  }
};

const locationBounds = computed(() =>
  officeList.value.map(el => [Number(el.longitude.toFixed(1)), Number(el.latitude.toFixed(1))]),
);

const dataSource = computed(() => {
  const videoLink = currentOffice.value?.video_link;
  if (videoLink) {
    return {
      html: `${videoLink}`,
    };
  }
  return {};
});

const openVideoModal = () => {
  if (dataSource.value && dataSource.value.html) {
    const { openMediaModal } = useMediaModal([dataSource.value]);
    openMediaModal(activeTab.value);
  }
};

watch(
  dataSource,
  () => {
    const { destroyMediaModal } = useMediaModal([dataSource.value]);
    destroyMediaModal();
  },
  { deep: true },
);
</script>
